import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_View_DashSelfServeOnboarding } from "@mirage/analytics/events/types/view_dash_self_serve_onboarding";
import { ProfilingQuestions } from "@mirage/growth/components/ProfilingQuestions/ProfilingQuestions";
import { useDoOnceOnMount } from "@mirage/shared/hooks/useDoOnceOnMount";
import { RoutePath } from "@mirage/webapp/routeTypes";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  isLargeCompany,
  useOnboardingSurveys,
} from "../hooks/useOnboardingSurveys";
import { useSelfServeOnboardingState } from "../hooks/useSelfServeOnboardingState";
import { SelfServeOnboardingStep } from "./types";

import { getDashProfilingIndustryFromString } from "@mirage/analytics/events/enums/dash_profiling_industry-utils";
import type { DashProfilingTeamSize } from "@mirage/analytics/events/enums/dash_profiling_team_size";
import { getDashProfilingTeamSizeFromString } from "@mirage/analytics/events/enums/dash_profiling_team_size-utils";
import { getDashProfilingWorkTypeFromString } from "@mirage/analytics/events/enums/dash_profiling_work_type-utils";
import { PAP_Select_SelfServeProfilingIndustry } from "@mirage/analytics/events/types/select_self_serve_profiling_industry";
import { PAP_Select_SelfServeProfilingTeamSize } from "@mirage/analytics/events/types/select_self_serve_profiling_team_size";
import { PAP_Select_SelfServeProfilingWorkType } from "@mirage/analytics/events/types/select_self_serve_profiling_work_type";
import type { OnboardingSurveyAnswer } from "@mirage/service-onboarding-surveys/service/types";
import { useSelfServeProfilingSurveyName } from "../hooks/useSelfServeProfilingSurveyName";

type Props = {
  isAdmin: boolean;
  canProvisionDashFreemiumTeam: boolean;
  onValidationChange: (valid: boolean) => void;
};

export const OnboardingProfilingStep = forwardRef(
  (
    { isAdmin, canProvisionDashFreemiumTeam, onValidationChange }: Props,
    ref,
  ) => {
    const { reportPapEvent } = useMirageAnalyticsContext();
    const hasLoadedSurveyResponses = useRef(false);

    const { surveyName } = useSelfServeProfilingSurveyName(
      isAdmin,
      canProvisionDashFreemiumTeam,
    );

    const {
      questions,
      surveyResponses,
      submitSurvey,
      getGroupedSurveyResponses,
    } = useOnboardingSurveys(surveyName);

    const { surveyResponses: surveyResponsesState, setSurveyResponses } =
      useSelfServeOnboardingState();
    const navigate = useNavigate();
    const [internalValues, setInternalValues] = useState<
      OnboardingSurveyAnswer[]
    >([]);

    useImperativeHandle(ref, () => ({
      submitSurvey: () => handleSubmitSurvey(internalValues),
    }));

    const getProfilingTeamSizeForLog = (
      surveyAnswer?: string,
    ): DashProfilingTeamSize | undefined => {
      if (!surveyAnswer) return undefined;

      let surveyAnswerStr = surveyAnswer
        .replace(" ", "_")
        .replace("-", "_")
        .toLowerCase();
      if (surveyAnswerStr === "1000+") {
        surveyAnswerStr = "size_1000_plus";
      } else if (surveyAnswerStr !== "just_me") {
        surveyAnswerStr = `size_${surveyAnswerStr}`;
      }

      return getDashProfilingTeamSizeFromString(surveyAnswerStr);
    };

    const logProfilingAnswerChange = useCallback(
      (newValue: OnboardingSurveyAnswer) => {
        switch (newValue.surveyQuestionId) {
          case "COMPANY_SIZE_V1":
            reportPapEvent(
              PAP_Select_SelfServeProfilingTeamSize({
                featureLine: "onboarding",
                dashProfilingTeamSize: getProfilingTeamSizeForLog(
                  newValue.surveyAnswer,
                ),
              }),
            );
            break;
          case "INDUSTRY_V1":
            reportPapEvent(
              PAP_Select_SelfServeProfilingIndustry({
                featureLine: "onboarding",
                dashProfilingIndustry: newValue.surveyAnswer
                  ? getDashProfilingIndustryFromString(newValue.surveyAnswer)
                  : undefined,
              }),
            );
            break;
          case "KIND_OF_WORK_V1":
            reportPapEvent(
              PAP_Select_SelfServeProfilingWorkType({
                featureLine: "onboarding",
                dashProfilingWorkType: newValue.surveyAnswer
                  ? getDashProfilingWorkTypeFromString(newValue.surveyAnswer)
                  : undefined,
              }),
            );
        }
      },
      [],
    );

    const handleProfilingAnswersChange = useCallback(
      (
        surveyResponses: OnboardingSurveyAnswer[],
        newValue?: OnboardingSurveyAnswer,
      ) => {
        setInternalValues(surveyResponses);

        if (newValue) {
          logProfilingAnswerChange(newValue);
        }

        if (isLargeCompany(surveyResponses)) {
          onValidationChange(true);
          return;
        }
        const hasCompletedAllQuestions =
          surveyResponses.filter(
            (response) => response.surveyAnswer?.trim() !== "",
          ).length >= questions.length;
        onValidationChange(hasCompletedAllQuestions);
      },
      [logProfilingAnswerChange, onValidationChange, questions.length],
    );

    useEffect(() => {
      const responsesToUse = surveyResponsesState.has(surveyName)
        ? surveyResponsesState.get(surveyName)
        : surveyResponses;

      if (responsesToUse?.length && !hasLoadedSurveyResponses.current) {
        // This ensures that the survey responses are not reset to the stored values on window resize
        hasLoadedSurveyResponses.current = true;

        setInternalValues(responsesToUse);
        handleProfilingAnswersChange(responsesToUse);
      }
    }, [
      handleProfilingAnswersChange,
      surveyResponses,
      surveyResponsesState,
      surveyName,
    ]);

    useDoOnceOnMount(() => {
      reportPapEvent(
        PAP_View_DashSelfServeOnboarding({
          selfServeIsTeamAdmin: isAdmin,
          onboardingStep: "profiling",
          featureLine: "onboarding",
        }),
      );
    });

    const getNavigatePathForStep = (step: SelfServeOnboardingStep): string => {
      return RoutePath.SETUP_ADMIN_SELF_SERVE.replace(":step", step);
    };

    const handleSubmitSurvey = async (responses: OnboardingSurveyAnswer[]) => {
      setInternalValues(responses);

      if (isLargeCompany(responses)) {
        navigate(getNavigatePathForStep(SelfServeOnboardingStep.LARGE_TEAM));
        // Do not submit, or save survey responses for large teams
        setSurveyResponses(surveyName, []);
        return false;
      }
      setSurveyResponses(surveyName, responses);

      const { userBasedResponses } = getGroupedSurveyResponses(responses);
      await submitSurvey(userBasedResponses.filter(Boolean));

      return true;
    };
    return (
      <ProfilingQuestions
        questions={questions}
        values={internalValues}
        skipRemaining={isLargeCompany(internalValues)}
        onChange={handleProfilingAnswersChange}
      />
    );
  },
);

OnboardingProfilingStep.displayName = "OnboardingProfilingStep";
